import React, { createContext, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDashboardPermissions } from '@components/utils/DashboardPermissions';
import { ConfirmModal, EmptyState, PositionChangeModal, useMediaQueryContext, useModalContext, usePagination, useToastsContext, } from '@fourthwall/components';
import { useSeriesQuery } from '@modules/Dashboard/Memberships/modules/Series/hooks/useSeriesQuery';
import { reverse, routing } from '@utils/routing';
import { useDeleteSingleSeriesMutation } from '../../../SingleSeries/hooks/useDeleteSingleSeriesMutation';
import { SeriesCell } from '../../components/SeriesCell';
import { useReorderSingleSeriesMutation } from '../../hooks/useReorderSingleSeriesMutation';
import { getCurrentSeriesPositionIndex, getUpdatedSeriesPositionIndex } from './utils';
export const SeriesContext = createContext(undefined);
export const SeriesProvider = ({ children }) => {
    const { hasPermission } = useDashboardPermissions();
    const [currentPage, setCurrentPage] = usePagination();
    const modal = useModalContext();
    const toast = useToastsContext();
    const media = useMediaQueryContext();
    const { seriesQuery } = useSeriesQuery([{ page: currentPage, postsType: 'video', limit: 25 }]);
    const { reorderSingleSeriesMutation } = useReorderSingleSeriesMutation();
    const { deleteSingleSeriesMutation } = useDeleteSingleSeriesMutation();
    const getSingleSeriesLink = ({ id }) => ({
        pathname: reverse(routing.memberships.series.singleSeries.self, { singleSeriesId: id }),
        search: location.search,
    });
    const handleChangePosition = useCallback((singleSeries) => {
        if (!seriesQuery.data)
            return;
        modal.open(PositionChangeModal, {
            title: 'Change series position',
            maxPosition: seriesQuery.data.pagination.total,
            currentPosition: getCurrentSeriesPositionIndex(singleSeries.id, seriesQuery.data.series, currentPage, 9),
            onSubmit: (formValues) => {
                const { type = 'EXACT', exactPosition } = formValues;
                reorderSingleSeriesMutation.mutateAsync([
                    {
                        id: singleSeries.id,
                    },
                    {
                        position: getUpdatedSeriesPositionIndex(type, exactPosition, seriesQuery.data.pagination.total),
                    },
                ], {
                    onSuccess: () => {
                        seriesQuery.refetch();
                    },
                });
            },
        });
    }, [seriesQuery, modal, reorderSingleSeriesMutation, currentPage]);
    const handleDelete = useCallback((singleSeries) => {
        modal.open(ConfirmModal, {
            title: 'Delete series',
            text: 'Are you sure you want to delete this series?',
            onConfirm: () => {
                deleteSingleSeriesMutation.mutateAsync([{ id: singleSeries.id }], {
                    onSuccess: () => {
                        seriesQuery.refetch();
                        toast.notify('Series deleted successfully', {
                            type: 'info',
                        });
                    },
                });
            },
        });
    }, [seriesQuery, modal, deleteSingleSeriesMutation, toast]);
    const tableHeaders = useMemo(() => [
        {
            id: 'series',
            label: 'Series',
            render: (singleSeries) => {
                return (React.createElement(SeriesCell, { title: singleSeries.title, quantity: singleSeries.episodesCount, thumbnailUrl: singleSeries.thumbnail?.url || singleSeries.coverImage?.url }));
            },
        },
    ], []);
    const table = useMemo(() => ({
        data: seriesQuery.data?.series || [],
        headers: tableHeaders,
        isLoading: seriesQuery.isFetching ||
            reorderSingleSeriesMutation.isLoading ||
            deleteSingleSeriesMutation.isLoading,
        sortable: true,
        actions: [
            {
                label: 'Change position',
                onClick: (singleSeries) => handleChangePosition(singleSeries),
            },
            {
                label: 'Delete',
                onClick: (singleSeries) => handleDelete(singleSeries),
            },
        ],
        onPositionChange: (oldIndex, newIndex) => {
            reorderSingleSeriesMutation.mutateAsync([
                {
                    id: seriesQuery.data?.series[oldIndex].id,
                },
                {
                    position: newIndex,
                },
            ], {
                onSuccess: () => {
                    seriesQuery.refetch();
                },
            });
        },
        emptyComponent: (React.createElement(EmptyState, { variant: media.tablet ? 'primary' : 'secondary', title: "You haven\u2019t created any video series yet", label: hasPermission('memberships.video.create') ? 'Add new video series' : undefined, to: routing.memberships.series.create })),
        rowComponent: Link,
        getRowLink: (singleSeries) => ({
            pathname: reverse(routing.memberships.series.singleSeries.self, {
                singleSeriesId: singleSeries.id,
            }),
            search: location.search,
        }),
    }), [
        seriesQuery,
        media,
        tableHeaders,
        hasPermission,
        reorderSingleSeriesMutation,
        handleChangePosition,
        deleteSingleSeriesMutation,
        handleDelete,
    ]);
    const list = useMemo(() => ({
        data: seriesQuery.data?.series || [],
        isLoading: seriesQuery.isInitialLoading,
        emptyComponent: (React.createElement(EmptyState, { variant: media.tablet ? 'primary' : 'secondary', title: "You haven\u2019t created any video series yet", label: hasPermission('memberships.video.create') ? 'Add new video series' : undefined, to: routing.memberships.series.create })),
        renderItem: (singleSeries) => (React.createElement(SeriesCell, { title: singleSeries.title, quantity: singleSeries.episodesCount, thumbnailUrl: singleSeries.thumbnail?.url || singleSeries.coverImage?.url, options: [
                {
                    label: 'Change position',
                    onClick: () => {
                        handleChangePosition(singleSeries);
                    },
                },
            ] })),
        getItemLink: getSingleSeriesLink,
    }), [seriesQuery, media, hasPermission, handleChangePosition]);
    const value = useMemo(() => ({
        table,
        list,
        pageCount: seriesQuery.data?.pagination?.pages || 1,
        currentPage,
        setCurrentPage,
    }), [table, list, currentPage, setCurrentPage, seriesQuery]);
    return React.createElement(SeriesContext.Provider, { value: value }, children);
};
